import React from 'react';
import * as _ from 'lodash';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import {Format} from '../../../../common/utils';
import {isNewPaycheck} from '../../utils/isNewPaycheck';
import {Summary} from '../../../../common/types';
import styles from './Calculation.module.scss';

export interface CalculationProps extends Partial<Summary> {
  commissionPercents: string;
}

export interface CalculationItemProps {
  label: React.ReactNode;
  value: React.ReactNode;
  classname?: string;
}

const isValid = (value: unknown): value is number | string => {
  return (typeof value === 'string' && value.replace(/0{2,}/g, '0') !== '0')
    || (typeof value === 'number' && value !== 0);
};

const CalculationItem = ({label, value, classname}: CalculationItemProps) => {
  return (
    <div className={classNames(styles.calculationItem, classname)}>
      <div>{label}</div>
      <div>{value}</div>
      <div>₪</div>
    </div>
  );
};

const Calculation = ({
                       linesTotal,
                       expensesWithVat,
                       expensesWithoutVat,
                       exchangeFee,
                       creditCardCommission,
                       vat,
                       commission,
                       commissionPercents,
                       employerCost,
                       brutto,
                       netto,
                       compensations,
                       linesTotalWithoutVat,
                       employerEducationFund,
                       employerMandatoryPension,
                       employerProvidentFund,
                       splitBrutto,
                       paycheckSplits,
                       incomeTax,
                       incomeTaxOffset,
                       employeeEducationFund,
                       employeeMandatoryPension,
                       employeeProvidentFund,
                       advancePayment,
                       nationalInsurance,
                       nationalInsuranceDebt,
                       supplierExpensesRefund,
                       expensesRefund,
                       toBePaid,
                       pensionManagementCommission,
                       paycheck,
                       lastPaycheck
                     }: CalculationProps) => {
  const prefix = 'transfers-popup-details-calculation';
  const showPensionManagementCommission = isNewPaycheck(paycheck, lastPaycheck) && _.some([
    employerEducationFund,
    employerMandatoryPension,
    employeeEducationFund,
    employeeMandatoryPension
  ]);

  return (
    <div className={styles.calculationContainer}>
      <CalculationItem
        label={<FormattedMessage id={`${prefix}-AmountVAT`} defaultMessage={'Amount with VAT'}/>}
        value={Format.displayFloatThousandsWithCommas(linesTotal || 0)}
        classname={styles.bold}
      />
      {isValid(expensesWithVat) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-ExpensesVAT`} defaultMessage={'Expenses with VAT'}/>}
          value={Format.displayFloatThousandsWithCommas(expensesWithVat)}
        />
      )}
      {isValid(vat) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-VAT`} defaultMessage={'VAT'}/>}
          value={Format.displayFloatThousandsWithCommas(vat)}
        />
      )}
      {isValid(linesTotalWithoutVat) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-linesTotalWithoutVat`} defaultMessage={'Amount before VAT'}/>}
          value={Format.displayFloatThousandsWithCommas(linesTotalWithoutVat)}
          classname={styles.bold}
        />
      )}
      {isValid(expensesWithoutVat) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-expensesWithoutVat`} defaultMessage={'Expenses without VAT'}/>}
          value={Format.displayFloatThousandsWithCommas(expensesWithoutVat)}
        />
      )}
      {isValid(exchangeFee) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-exchangeFee`} defaultMessage={'Exchange Fee'}/>}
          value={Format.displayFloatThousandsWithCommas(exchangeFee)}
        />
      )}
      {isValid(creditCardCommission) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-creditCardCommission`} defaultMessage={'Credit Card Commission'}/>}
          value={Format.displayFloatThousandsWithCommas(creditCardCommission)}
        />
      )}
      {isValid(commission) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-Commission`} defaultMessage={'Commission {value}'}
                                   values={{value: commissionPercents}}/>}
          value={Format.displayFloatThousandsWithCommas(commission)}
        />
      )}
      {isValid(pensionManagementCommission) && showPensionManagementCommission && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-pensionManagementCommission`}
                                   defaultMessage={'Pension management commission'}/>}
          value={Format.displayFloatThousandsWithCommas(0 - pensionManagementCommission)}
        />
      )}
      {isValid(employerCost) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-EmployerCost`} defaultMessage={`Employer cost`}/>}
          value={Format.displayFloatThousandsWithCommas(employerCost)}
          classname={styles.bold}
        />
      )}
      {isValid(compensations) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-Compensations`} defaultMessage={'Compensations'}/>}
          value={Format.displayFloatThousandsWithCommas(compensations)}
        />
      )}
      {isValid(employerEducationFund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employerEducationFund`} defaultMessage={'Employer Education Fund'}/>}
          value={Format.displayFloatThousandsWithCommas(employerEducationFund)}
        />
      )}
      {isValid(employerMandatoryPension) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employerMandatoryPension`}
                                   defaultMessage={'Employer Mandatory Pension'}/>}
          value={Format.displayFloatThousandsWithCommas(employerMandatoryPension)}
        />
      )}
      {isValid(employerProvidentFund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employerProvidentFund`} defaultMessage={'Employer Provident Fund'}/>}
          value={Format.displayFloatThousandsWithCommas(employerProvidentFund)}
        />
      )}
      <CalculationItem
        label={<FormattedMessage id={`${prefix}-Brutto`} defaultMessage={'Brutto'}/>}
        value={Format.displayFloatThousandsWithCommas(brutto || 0)}
        classname={styles.bold}
      />
      {isValid(splitBrutto) && isValid(paycheckSplits) && (paycheckSplits > 1) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-splitBrutto`} defaultMessage={'Split brutto (1/{splits})'}
                                   values={{splits: paycheckSplits}}/>}
          value={Format.displayFloatThousandsWithCommas(splitBrutto)}
        />
      )}
      {isValid(incomeTax) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-incomeTax`} defaultMessage={'Income Tax'}/>}
          value={Format.displayFloatThousandsWithCommas(incomeTax)}
        />
      )}
      {isValid(incomeTaxOffset) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-incomeTaxOffset`} defaultMessage={'Income Tax Offset'}/>}
          value={Format.displayFloatThousandsWithCommas(incomeTaxOffset)}
        />
      )}
      {isValid(employeeEducationFund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employeeEducationFund`} defaultMessage={'Employee Education Fund'}/>}
          value={Format.displayFloatThousandsWithCommas(employeeEducationFund)}
        />
      )}
      {isValid(employeeMandatoryPension) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employeeMandatoryPension`}
                                   defaultMessage={'Employee Mandatory Pension'}/>}
          value={Format.displayFloatThousandsWithCommas(employeeMandatoryPension)}
        />
      )}
      {isValid(employeeProvidentFund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-employeeProvidentFund`} defaultMessage={'Employee Provident Fund'}/>}
          value={Format.displayFloatThousandsWithCommas(employeeProvidentFund)}
        />
      )}
      <CalculationItem
        label={<FormattedMessage id={`${prefix}-Netto`} defaultMessage={'Netto'}/>}
        value={Format.displayFloatThousandsWithCommas(netto || 0)}
        classname={styles.bold}
      />
      {isValid(advancePayment) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-advancePayment`} defaultMessage={'Advance Payment'}/>}
          value={Format.displayFloatThousandsWithCommas(advancePayment)}
        />
      )}
      {isValid(nationalInsurance) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-nationalInsurance`} defaultMessage={'National Insurance'}/>}
          value={Format.displayFloatThousandsWithCommas(nationalInsurance)}
        />
      )}
      {isValid(nationalInsuranceDebt) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-nationalInsuranceDebt`} defaultMessage={'National Insurance Debt'}/>}
          value={Format.displayFloatThousandsWithCommas(nationalInsuranceDebt)}
        />
      )}
      {isValid(supplierExpensesRefund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-supplierExpensesRefund`}
                                   defaultMessage={'Supplier Expenses Refund'}/>}
          value={Format.displayFloatThousandsWithCommas(supplierExpensesRefund)}
        />
      )}
      {isValid(expensesRefund) && (
        <CalculationItem
          label={<FormattedMessage id={`${prefix}-expensesRefund`} defaultMessage={'Expenses Refund'}/>}
          value={Format.displayFloatThousandsWithCommas(expensesRefund)}
        />
      )}
      <CalculationItem
        label={<FormattedMessage id={`${prefix}-toBePaid`} defaultMessage={'To be paid'}/>}
        value={Format.displayFloatThousandsWithCommas(toBePaid || 0)}
        classname={styles.totalAmount}
      />
    </div>
  );
};

export default Calculation;
